/**
 * The Tell Us box (2026-08-29) — one composer, two doors.
 *
 * Until now the only way to tell us anything was a mailto: link on the
 * splash page. On an iPad that hands you off to Mail, loses the page you
 * were on, and asks for a letter when you wanted to say one line. Most
 * people just don't. This is that one line, sent from inside the app.
 *
 * It lives here rather than inside the footer because the footer is not
 * always reachable: Discover loads another page of cards whenever you come
 * within 300px of the bottom (discover.js), so on the app's busiest screen
 * the end of the document keeps moving away from you. The footer is still
 * the natural door on every page that ENDS — a yoink, a place, a profile,
 * a list — and the Help dialog carries the same button for everywhere else.
 * Both call openTellUs().
 *
 * Sending is two steps, in this order:
 *   1. submit_feedback() (SECURITY DEFINER RPC, 20260829_feedback.sql)
 *      writes the row and returns its id. The message is SAVED at this
 *      point — everything after is delivery.
 *   2. a POST to api/notifications.php {type:'feedback', id} emails the
 *      admin recipients. It re-reads the row server-side with the service
 *      key, so the email says what the database says, and it stamps
 *      notified_at so a replayed POST cannot send twice.
 * Step 2 failing is not the sender's problem and never shown to them: the
 * message is in the Feedback tab either way.
 */
import * as gateway from '../core/gateway.js';
import { getApiBase } from '../core/config.js';
import { track } from '../core/analytics.js';

const MAX_LENGTH = 4000;

/** Trim + length check, shared by the form and its tests. */
export function validateMessage(text) {
  const message = (text || '').trim();
  if (!message) return { ok: false, error: 'Type something first — even one line helps.' };
  if (message.length > MAX_LENGTH) {
    return { ok: false, error: `That's ${message.length} characters — the box holds ${MAX_LENGTH}.` };
  }
  return { ok: true, message };
}

/**
 * Write the message, then ask the server to email it.
 *
 * Resolves { ok, error }. The email step is deliberately swallowed: once
 * the RPC returns an id the message is durable, and telling someone their
 * note failed because Resend was down would only make them send it again.
 */
export async function sendFeedback({ message, contactEmail = '', pagePath = '', userAgent = '' } = {}) {
  const { data, error } = await gateway.rawRpc('submit_feedback', {
    params: {
      p_message: message,
      p_contact_email: contactEmail || null,
      p_page_path: pagePath || null,
      p_user_agent: userAgent || null,
    },
  });
  if (error) {
    // A 404 here means 20260829_feedback.sql hasn't been applied yet
    // (docs/SCHEMA-STATE.md — migrations are run by hand). Say something
    // a person can act on rather than "HTTP 404".
    const notInstalled = /404/.test(String(error.message || ''));
    return {
      ok: false,
      error: notInstalled
        ? 'The message box isn’t switched on yet. Email giro@yoinkadventures.com and it’ll get there.'
        : 'Couldn’t send that just now. Please try again in a moment.',
    };
  }
  const id = Array.isArray(data) ? data[0] : data;
  try {
    await fetch(`${getApiBase()}/notifications.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'feedback', id }),
    });
  } catch {
    // Saved is sent, as far as the sender is concerned.
  }
  return { ok: true, id };
}

/**
 * Open the composer. Built on demand and removed on close, like the admin
 * confirm dialog — there is no state worth keeping between two messages,
 * and a torn-down dialog cannot go stale when someone signs in or out.
 *
 * @param {object} ctx - { store, router }; both optional in tests.
 */
export function openTellUs({ store = null, router = null } = {}) {
  const dialog = document.createElement('dialog');
  dialog.className = 'v2-auth-dialog v2-tell-dialog';
  dialog.setAttribute('data-tell-dialog', '');
  dialog.setAttribute('aria-labelledby', 'tell-title');
  dialog.innerHTML = '<h2 class="v2-dialog-title" id="tell-title">Tell us</h2><div data-tell-body></div>';
  document.body.appendChild(dialog);

  const view = () => router?.parse?.()?.name || '';
  let sending = false;

  // A native modal <dialog> does NOT stop the page behind it scrolling on
  // iOS Safari, and a modal you can scroll out from under doesn't read as
  // one. Restored on close, whichever way it closes.
  const bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  function close() {
    document.body.style.overflow = bodyOverflow;
    if (typeof dialog.close === 'function') dialog.close();
    dialog.remove();
  }

  function showError(text) {
    const box = dialog.querySelector('[data-tell-error]');
    if (!box) return;
    box.textContent = text;
    box.hidden = !text;
  }

  /** The form, and after a successful send, the thank-you in its place. */
  function renderBody(sent = false) {
    const body = dialog.querySelector('[data-tell-body]');
    if (sent) {
      body.innerHTML = `
        <p class="tell-thanks">Got it — thank you. It went straight to the ranger station,
          and we read every one.</p>
        <div class="tell-actions tell-actions-done">
          <button type="button" class="v2-btn-dialog-close" data-tell-close>Close</button>
        </div>`;
      body.querySelector('[data-tell-close]')?.focus();
      return;
    }
    const user = store?.get?.().user;
    // The fields scroll, the buttons don't. On a phone with the keyboard
    // up there are ~350px of visible page left: a plain scrolling dialog
    // puts Send below the fold, and the one thing that must never be
    // hunted for is the button that sends the message.
    body.innerHTML = `
      <form data-tell-form novalidate>
        <div class="tell-fields">
          <p class="tell-intro">One line is plenty. It lands with the page you were on, so you
            don’t have to describe where you were.</p>
          <label class="tell-label" for="tell-message">Your message</label>
          <textarea id="tell-message" name="message" class="tell-textarea" rows="5"
            maxlength="${MAX_LENGTH}" required
            placeholder="What’s working, what isn’t, what you wish it did…"></textarea>
          ${user ? '' : `
          <label class="tell-label" for="tell-email">Email <span class="tell-optional">(optional — only if you want a reply)</span></label>
          <input id="tell-email" name="email" class="tell-input" type="email"
            autocomplete="email" inputmode="email" placeholder="you@example.com">`}
          <p class="tell-error" data-tell-error hidden role="alert"></p>
        </div>
        <div class="tell-actions">
          <button type="button" class="v2-btn-dialog-close" data-tell-close>Cancel</button>
          <button type="submit" class="v2-btn-leather tell-send" data-tell-send>Send</button>
        </div>
      </form>`;
    body.querySelector('#tell-message')?.focus();
  }

  async function submit(event) {
    event.preventDefault();
    if (sending) return;
    const form = dialog.querySelector('[data-tell-form]');
    if (!form) return;
    const check = validateMessage(form.elements.message?.value);
    if (!check.ok) return showError(check.error);
    showError('');

    sending = true;
    const sendBtn = form.querySelector('[data-tell-send]');
    if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; }

    const result = await sendFeedback({
      message: check.message,
      contactEmail: form.elements.email?.value || '',
      pagePath: `${window.location.pathname}${window.location.search}`,
      userAgent: navigator.userAgent,
    });

    sending = false;
    if (!result.ok) {
      if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send'; }
      return showError(result.error);
    }
    track('feedback_sent', { view: view() });
    renderBody(true);
  }

  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-tell-close]')) return close();
    // Backdrop tap. A native <dialog> reports the dialog itself as the
    // target when the click landed outside its box.
    if (event.target === dialog) close();
  });
  dialog.addEventListener('submit', submit);
  dialog.addEventListener('close', () => {
    document.body.style.overflow = bodyOverflow;
    dialog.remove();
  });

  renderBody(false);
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
  track('feedback_open', { view: view() });
  return dialog;
}
